import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom-v5-compat';
import { useTheme } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';

import {
    duplicateInvoice,
    shareInvoice,
    updateClient,
    getInvoices
} from '@/modules/dataWrangler/dataWrangler';
import {
    SendIcon,
    DuplicateIcon,
    DuplicateIconPopup,
    DownloadIcon
} from '@/resources/icons';
import { PDF_CREATED } from '@/constants';
import { delayAwait, downloadPdf } from '@/util';
import { LottieDownloadLoading, LottieSendingLoading } from '@/lottie';
import { clientModel, clientDefaultModel } from '@/models';
import { useToastNotification, useSubscription, useUrlParams } from '@/hooks';

import NoEmailModal from '../../NoEmailModal';
import LottieModal from '../../LottieModal';
import WarnConfirmDialog from '../../WarnConfirmDialog';
import ActionIconWrapper from '../ActionIconWrapper';

const useStyles = makeStyles(() => ({
    send: {
        width: 19,
        height: 16
    },
    cleanIcon: { fill: 'none !important' },
    convertModalIcon: { width: 91, height: 91 }
}));

/**
 * Send Icon with dispatch attached
 * @param {object} client Client object of client to send to.
 * @param {string} id ID of the invoice to send.
 */

export const SendInvoiceIcon = ({ client, id }) => {
    let timeoutManager;
    const [modalActive, setModal] = useState(false);
    const [showNoEmailModal, setShowNoEmailModal] = useState(false);
    const { triggerToast } = useToastNotification();
    const params = useParams();
    const { getUrlParams } = useUrlParams();
    const classes = useStyles();

    useEffect(() => clearTimeout(timeoutManager), [timeoutManager]);

    const manageEmailSend = async (email) => {
        const startTime = Date.now();
        const currentUrlParams = getUrlParams();
        setModal(true);
        const success = await shareInvoice(id, 'email', email);
        await delayAwait(startTime, 2000);
        if (success) {
            setModal(false);

            // If we're on the client page, we only want to return the invoices
            // for that client.
            if (params?.clientId) {
                currentUrlParams.client = params.clientId;
                currentUrlParams.prefilters = 'clientId';
            }

            getInvoices(currentUrlParams);
            triggerToast({
                message: `Invoice #${
                    success.document?.customLabel || success.referenceNumber
                } Sent`,
                action: 'send',
                icon: <SendIcon className={classes.send} />
            });
        }
    };

    const sendInvoice = async () => {
        if (!client.emailAddress) {
            setShowNoEmailModal(true);
        } else {
            manageEmailSend();
        }
    };

    return (
        <>
            <ActionIconWrapper
                action="send"
                label="Send"
                listOnTablet
                onClick={() => {
                    sendInvoice(id);
                }}
                tooltip="Send Invoice"
            />
            <LottieModal
                title="Sending Invoice..."
                backgroundColor="#F5F9FF"
                open={modalActive}
            >
                <LottieSendingLoading />
            </LottieModal>
            <NoEmailModal
                open={showNoEmailModal}
                onClose={async (emailAddress, saveToClient) => {
                    if (emailAddress === undefined) {
                        setShowNoEmailModal(false);
                        return;
                    }
                    setShowNoEmailModal(false);

                    if (saveToClient) {
                        const updatedClient = {
                            ...client,
                            emailAddress
                        };
                        updateClient(updatedClient);
                    }

                    manageEmailSend(emailAddress);
                }}
            />
        </>
    );
};

SendInvoiceIcon.propTypes = {
    id: PropTypes.string.isRequired,
    client: clientModel
};

SendInvoiceIcon.defaultProps = clientDefaultModel;

/**
 * View Icon with link attached.
 * @param {string} id ID of the invoice to view.
 */
export const ViewInvoiceIcon = ({ id }) => (
    <ActionIconWrapper
        action="view"
        label="View"
        link={`/invoice/${id}`}
        tooltip="View Invoice"
    />
);

ViewInvoiceIcon.propTypes = {
    id: PropTypes.string.isRequired
};

/**
 * Download Icon with dispatch attached
 * @param {string} id ID of the invoice to download.
 * @param {string} label Name to append to "invoice-XXX.pdf".
 */
export const DownloadInvoiceIcon = ({ id, label }) => {
    const [modalActive, setModal] = useState(false);
    const { triggerToast } = useToastNotification();
    const params = useParams();
    const { getUrlParams } = useUrlParams();
    const classes = useStyles();
    let timeoutManager;

    useEffect(() => clearTimeout(timeoutManager), [timeoutManager]);

    const downloadInvoice = async () => {
        const startTime = Date.now();
        const currentUrlParams = getUrlParams();
        setModal(true);
        const res = await shareInvoice(id, 'download');
        await delayAwait(startTime, 2000);
        setModal(false);
        if (res?.status === PDF_CREATED) {
            downloadPdf({ name: `invoice-${label}`, base64: res.pdf });

            // If we're on the client page, we only want to return the invoices
            // for that client.
            if (params?.clientId) {
                currentUrlParams.client = params.clientId;
                currentUrlParams.prefilters = 'clientId';
            }

            getInvoices(currentUrlParams);
            triggerToast({
                message: `Invoice Downloaded`,
                action: 'saved',
                icon: <DownloadIcon className={classes.cleanIcon} />
            });
        }
    };

    return (
        <>
            <ActionIconWrapper
                action="download"
                label="Download"
                listOnTablet
                onClick={() => {
                    downloadInvoice();
                }}
                tooltip="Download Invoice"
            />
            <LottieModal
                title="Downloading Invoice..."
                backgroundColor="#F5F9FF"
                open={modalActive}
            >
                <LottieDownloadLoading />
            </LottieModal>
        </>
    );
};

DownloadInvoiceIcon.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
};

/**
 * Edit Icon with dispatch attached
 * @param {string} id ID of the invoice to edit.
 */
export const EditInvoiceIcon = ({ id }) => (
    <ActionIconWrapper
        action="edit"
        label="Edit"
        link={`/invoice/${id}`}
        tooltip="Edit Invoice"
    />
);

EditInvoiceIcon.propTypes = {
    id: PropTypes.string.isRequired
};

/**
 * Duplicate Icon with dispatch attached
 * @param {string} id ID of the invoice to duplicate.
 * @param {string} label Invoice number or custom label to duplicate
 */
export const DuplicateInvoiceIcon = ({ id, label }) => {
    const theme = useTheme();
    const classes = useStyles();
    const navigate = useNavigate();
    const subscriptionData = useSubscription();

    const [duplicateDialogActive, setDuplicateDialogActive] = useState(false);

    const handleClick = () => {
        const subInfo = subscriptionData?.components?.document;

        const blockDocumentCreation =
            !subInfo?.enabled ||
            (subInfo?.limited && subInfo?.amountRemaining < 1);

        if (blockDocumentCreation) {
            // If the user has no more documents, remaining force them
            // to subscription page.
            navigate('/settings/subscription');
        } else {
            setDuplicateDialogActive(true);
        }
    };

    const { triggerToast } = useToastNotification();

    const handleConfirmation = async () => {
        setDuplicateDialogActive(false);

        const newInvoice = await duplicateInvoice(id);

        if (newInvoice) {
            triggerToast({
                message: `Invoice ${label} Duplicated`,
                action: 'duplicate',
                icon: <DuplicateIcon className={classes.cleanIcon} />
            });

            setTimeout(() => {
                navigate(`/invoice/${newInvoice.documentId}`);
            }, 1000);
        }
    };

    const handleCancellation = () => {
        setDuplicateDialogActive(false);
    };

    return (
        <>
            <ActionIconWrapper
                action="duplicate"
                label="Duplicate"
                listOnTablet
                onClick={() => {
                    handleClick(id);
                }}
                tooltip="Duplicate Invoice"
            />
            <WarnConfirmDialog
                onCancel={() => handleCancellation()}
                onConfirm={() => handleConfirmation()}
                customIcon={<DuplicateIconPopup />}
                confirmColor={theme.palette.primary.main}
                message="Are you sure you want to duplicate this invoice?"
                cancelText="Cancel"
                confirmText="Duplicate Invoice"
                open={duplicateDialogActive}
            />
        </>
    );
};

DuplicateInvoiceIcon.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
};
